import React from "react";
import { Dumbbell, Trash2 } from "lucide-react";

const SURFACE_OPTIONS_BY_SPORT = {
  // Sport-specific surface presets — the dropdown still lets users type free-text via the "Other" option.
  padel:      ["synthetic_turf", "concrete", "carpet", "porous"],
  tennis:     ["clay", "hard", "grass", "synthetic", "carpet"],
  pickleball: ["concrete", "asphalt", "synthetic", "indoor_wood"],
  badminton:  ["wood", "synthetic", "carpet"],
  squash:     ["wood", "plaster"],
  basketball: ["hardwood", "concrete", "synthetic"],
  football:   ["grass", "synthetic_turf", "indoor"],
  futsal:     ["wood", "concrete", "synthetic"],
  volleyball: ["sand", "wood", "synthetic"],
  golf:       ["grass", "synthetic_turf"],
  cricket:    ["grass", "matting"],
};

const _norm = (s = "") => s.toLowerCase().replace(/\s+/g, "_");

/**
 * Renders one sub-form per selected sport. Auto-syncs with the parent's
 * `selectedSports` array — when a sport is added, an empty config row appears;
 * when removed, the row vanishes.
 *
 * Props:
 *   - selectedSports: Array<{slug, name}>  the sports the parent has picked
 *   - configs: Array<SportConfig>          current config rows (parent state)
 *   - onChange(configs): writes back to the parent
 */
export default function SportConfigsForm({ selectedSports = [], configs = [], onChange }) {
  // Reconcile: ensure exactly one config per selected sport. Preserves any
  // existing values; drops stale rows when a sport is unselected.
  const reconciled = React.useMemo(() => {
    const bySlug = new Map(configs.map((c) => [c.sport_slug, c]));
    return selectedSports.map((s) =>
      bySlug.get(s.slug) || {
        sport_slug: s.slug, num_courts: "", surface: "", indoor: null,
        price_per_unit: "", currency: "EUR", notes: "",
      },
    );
  }, [selectedSports, configs]);

  // Push reconciled configs back to parent whenever the selection changes
  React.useEffect(() => {
    if (reconciled.length !== configs.length) {
      onChange(reconciled);
    }
  }, [reconciled, configs.length, onChange]);

  const update = (slug, key, value) => {
    onChange(reconciled.map((c) => c.sport_slug === slug ? { ...c, [key]: value } : c));
  };
  const drop = (slug) => {
    onChange(reconciled.filter((c) => c.sport_slug !== slug));
  };

  if (selectedSports.length === 0) {
    return (
      <div className="text-xs text-zinc-500 italic" data-testid="sport-configs-empty">
        Pick one or more sports above to configure their per-sport details (courts, surface, indoor/outdoor, price).
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sport-configs">
      {reconciled.map((cfg) => {
        const sport = selectedSports.find((s) => s.slug === cfg.sport_slug);
        const surfaceOptions = SURFACE_OPTIONS_BY_SPORT[cfg.sport_slug] || [];
        return (
          <div
            key={cfg.sport_slug}
            data-testid={`sport-config-${cfg.sport_slug}`}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Dumbbell size={14} className="text-[#00E676]" />
                <h4 className="font-heading text-sm font-bold uppercase tracking-wider">
                  {sport?.name || cfg.sport_slug}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => drop(cfg.sport_slug)}
                title="Remove this sport's config"
                data-testid={`sport-config-drop-${cfg.sport_slug}`}
                className="p-1.5 rounded-md border border-[#FF3B30]/20 text-[#FF3B30]/80 hover:bg-[#FF3B30]/10"
              >
                <Trash2 size={11} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Courts">
                <input
                  type="number" min={0} max={500}
                  value={cfg.num_courts ?? ""}
                  onChange={(e) => update(cfg.sport_slug, "num_courts", e.target.value === "" ? null : Number(e.target.value))}
                  data-testid={`sport-config-${cfg.sport_slug}-courts`}
                  className="pp-input w-full" placeholder="e.g. 4"
                />
              </Field>
              <Field label="Surface">
                <select
                  value={surfaceOptions.includes(_norm(cfg.surface || "")) ? _norm(cfg.surface || "") : (cfg.surface ? "_other" : "")}
                  onChange={(e) => {
                    if (e.target.value === "_other") update(cfg.sport_slug, "surface", "");
                    else update(cfg.sport_slug, "surface", e.target.value || null);
                  }}
                  data-testid={`sport-config-${cfg.sport_slug}-surface`}
                  className="pp-input w-full"
                >
                  <option value="">— select —</option>
                  {surfaceOptions.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  <option value="_other">Other (specify)</option>
                </select>
                {(cfg.surface && !surfaceOptions.includes(_norm(cfg.surface))) && (
                  <input
                    type="text"
                    value={cfg.surface}
                    onChange={(e) => update(cfg.sport_slug, "surface", e.target.value)}
                    data-testid={`sport-config-${cfg.sport_slug}-surface-other`}
                    className="pp-input w-full mt-1.5 text-xs" placeholder="e.g. red clay, slate"
                  />
                )}
              </Field>
              <Field label="Indoor / Outdoor">
                <select
                  value={cfg.indoor === true ? "indoor" : cfg.indoor === false ? "outdoor" : ""}
                  onChange={(e) => update(
                    cfg.sport_slug, "indoor",
                    e.target.value === "indoor" ? true : e.target.value === "outdoor" ? false : null,
                  )}
                  data-testid={`sport-config-${cfg.sport_slug}-indoor`}
                  className="pp-input w-full"
                >
                  <option value="">— either —</option>
                  <option value="indoor">Indoor</option>
                  <option value="outdoor">Outdoor</option>
                </select>
              </Field>
              <Field label="Price per court / hour">
                <input
                  type="number" min={0} step={0.5}
                  value={cfg.price_per_unit ?? ""}
                  onChange={(e) => update(cfg.sport_slug, "price_per_unit", e.target.value === "" ? null : Number(e.target.value))}
                  data-testid={`sport-config-${cfg.sport_slug}-price`}
                  className="pp-input w-full" placeholder="0.00"
                />
              </Field>
              <Field label="Currency">
                <select
                  value={cfg.currency || "EUR"}
                  onChange={(e) => update(cfg.sport_slug, "currency", e.target.value)}
                  data-testid={`sport-config-${cfg.sport_slug}-currency`}
                  className="pp-input w-full"
                >
                  {["EUR", "USD", "GBP", "CAD", "AUD", "MXN", "BRL", "JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Notes">
              <input
                type="text"
                value={cfg.notes || ""}
                onChange={(e) => update(cfg.sport_slug, "notes", e.target.value)}
                data-testid={`sport-config-${cfg.sport_slug}-notes`}
                className="pp-input w-full"
                placeholder="e.g. lights until 23:00, pro shop on site, lockers included"
              />
            </Field>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
